import {
  type DealPhase,
  type DealStatus,
  PHASE_LABELS,
  SOURCE_CATEGORY_LABELS,
  type SourceCategory,
  STATUS_LABELS,
} from '@/features/preinvest/types'
import { formatDatum } from '@/lib/format'
import type { Notification } from './types'

/** Dutch names per `ai_run_completed` payload `kind`; unknown kinds → "AI-run". */
const AI_KIND_LABELS: Record<string, string> = {
  gap_analysis: 'Gap-analyse',
  ic_scoring: 'IC-scoring',
  deep_research: 'Deep research',
  claims_dd: 'Claims-DD',
  followups: 'Follow-upvragen',
  journey_seed: 'Journey-opzet',
  journey_narrative: 'Journey-narratief',
  financial_dd_validation: 'Financiële DD-validatie',
  notulen_analysis: 'Notulen-analyse',
}

/** `dealflow.form_kind` labels for the `form_submitted` one-liner. */
const FORM_KIND_LABELS: Record<string, string> = {
  questionnaire: 'Vragenlijst',
  followup: 'Follow-upformulier',
}

/** spec-60: "Jesse {verb}" per bulk action; `{n}` becomes "12 deals". */
const BULK_VERBS = new Map<string, string>([
  ['advance', 'zette {n} door naar Kennismaking'],
  ['reject', 'wees {n} af'],
  ['park', 'parkeerde {n}'],
  ['restore', 'zette {n} terug'],
  ['reopen', 'heropende {n}'],
  ['assign', 'wees {n} toe'],
  ['classify', 'zette de bron van {n}'],
  ['rejection_reason', 'wijzigde de afwijsreden van {n}'],
])

/** spec-60: how we met a startup again (`startup_returned.origin`). */
const ORIGIN_LABELS = new Map<string, string>([
  ['website', 'website'],
  ['handmatig', 'handmatig toegevoegd'],
])

function phaseName(value: string | undefined): string | null {
  if (!value) return null
  return PHASE_LABELS[value as DealPhase] ?? value
}

/** `closed` is a spec-35 status outside the board's `DealStatus` union. */
const EXTRA_STATUS_LABELS: Record<string, string> = { closed: 'Gesloten' }

function statusName(value: string | undefined): string | null {
  if (!value) return null
  return STATUS_LABELS[value as DealStatus] ?? EXTRA_STATUS_LABELS[value] ?? value
}

/** "<prefix>: <task> · <startup>", each part left out when unknown. */
function taskLine(
  prefix: string,
  task: string | undefined,
  startup: string | null,
  bare = prefix,
): string {
  if (!task) return startup ? `${prefix}: ${startup}` : bare
  return `${prefix}: ${task}${startup ? ` · ${startup}` : ''}`
}

/**
 * Dutch one-liner per type, interpolating `payload.startup_name` (spec-35
 * §Frontend). Every payload field is optional: when the context is missing the
 * line degrades to a generic label, and an unknown type renders "Melding".
 */
export function notificationTitle(
  notification: Notification,
  /** Resolves a user id to a name (the team list); null when unknown. */
  ownerName: (id: string) => string | null = () => null,
): string {
  const payload = notification.payload
  const name = payload.startup_name?.trim() ? payload.startup_name.trim() : null
  const voor = name ? ` voor ${name}` : ''
  const door = name ? ` door ${name}` : ''
  switch (notification.type) {
    case 'startup_created':
      return name ? `Nieuwe startup: ${name}` : 'Nieuwe startup toegevoegd'
    case 'mention':
      return name ? `Genoemd in een notitie bij ${name}` : 'Genoemd in een notitie'
    case 'task_assigned':
      return taskLine('Taak voor jou', payload.task_title, name, 'Taak aan jou toegewezen')
    case 'deal_phase_changed': {
      const to = phaseName(payload.to_phase)
      if (name && to) return `${name} naar fase ${to}`
      if (to) return `Deal naar fase ${to}`
      return `Fase gewijzigd${voor}`
    }
    case 'deal_status_changed': {
      // spec-60: only an automatic restore on the return date notifies a
      // Geparkeerd → active move (a manual restore is silent).
      if (payload.from_status === 'latent' && payload.to_status === 'active') {
        const phase = phaseName(payload.phase)
        return `${name ?? 'Deal'} is terug uit Geparkeerd${phase ? ` (${phase})` : ''}`
      }
      const to = statusName(payload.to_status)
      if (name && to) return `Status van ${name} naar ${to.toLowerCase()}`
      if (to) return `Dealstatus naar ${to.toLowerCase()}`
      return `Dealstatus gewijzigd${voor}`
    }
    case 'deal_owner_changed': {
      const owner = payload.new_owner_id ? ownerName(payload.new_owner_id) : null
      if (name && owner) return `Eigenaar van ${name} is nu ${owner}`
      return name ? `Nieuwe eigenaar voor ${name}` : 'Nieuwe eigenaar voor een deal'
    }
    case 'nda_signed':
      return `NDA ondertekend${voor}`
    case 'deal_stalled': {
      // The date the deal entered the phase: a day count would freeze at notification time.
      const subject = name ?? 'Deal'
      const days = payload.days_in_phase
      const phase = phaseName(payload.phase)
      const created = new Date(notification.created_at).getTime()
      if (days != null && phase && !Number.isNaN(created)) {
        const since = new Date(created - days * 86_400_000).toISOString()
        return `${subject} staat sinds ${formatDatum(since)} in ${phase}`
      }
      return `${subject} staat al lang in dezelfde fase`
    }
    case 'startup_returned_after_rejection':
      return name
        ? `${name} meldt zich opnieuw na eerdere afwijzing`
        : 'Startup meldt zich opnieuw na eerdere afwijzing'
    case 'startup_returned': {
      const via = payload.origin ? (ORIGIN_LABELS.get(payload.origin) ?? payload.origin) : null
      const back = payload.previous_status === 'latent' ? ' en is terug uit Geparkeerd' : ''
      return `${name ?? 'Startup'} meldde zich opnieuw${via ? ` (${via})` : ''}${back}`
    }
    case 'deals_bulk_updated': {
      const who = payload.actor_name?.trim() || 'Iemand'
      const count = payload.count ?? 0
      const verb = (payload.action && BULK_VERBS.get(payload.action)) || 'werkte {n} bij'
      const detail = payload.detail ? ` (${payload.detail})` : ''
      return `${who} ${verb.replace('{n}', `${count} ${count === 1 ? 'deal' : 'deals'}`)}${detail}`
    }
    case 'leads_imported': {
      // spec-60 B2: "Jesse importeerde 37 leads (Event · Slush 2026): 31 nieuw, 6 bekend".
      const who = payload.actor_name?.trim() || 'Iemand'
      const count = payload.count ?? 0
      const category = payload.source_category
        ? (SOURCE_CATEGORY_LABELS[payload.source_category as SourceCategory] ??
          payload.source_category)
        : null
      const source = category
        ? ` (${payload.source_name ? `${category} · ${payload.source_name}` : category})`
        : ''
      return `${who} importeerde ${count} ${count === 1 ? 'lead' : 'leads'}${source}: ${payload.created ?? 0} nieuw, ${payload.known ?? 0} bekend`
    }
    case 'ai_run_completed': {
      const kind = (payload.kind && AI_KIND_LABELS[payload.kind]) || 'AI-run'
      // `success: false` = failed or empty run — never a false "klaar!".
      return `${kind} ${payload.success === false ? 'mislukt' : 'klaar'}${voor}`
    }
    case 'form_submitted': {
      const kind = (payload.form_kind && FORM_KIND_LABELS[payload.form_kind]) || 'Formulier'
      return `${kind} ingediend${door}`
    }
    case 'financial_dd_submitted':
      return `Financiële DD ingediend${door}`
    case 'document_uploaded':
      return `Document geüpload${door}`
    case 'task_due_soon':
      return taskLine('Taak verloopt binnen 24 uur', payload.task_title, name)
    case 'task_overdue':
      return taskLine('Taak over de deadline', payload.task_title, name)
    case 'mail_received':
      if (name) return `Nieuwe mail van ${name}`
      return payload.from_email ? `Nieuwe mail van ${payload.from_email}` : 'Nieuwe mail ontvangen'
    case 'portal_form_submitted': {
      if (payload.form_kind && payload.form_kind !== 'biweekly')
        return `Portal-formulier ingeleverd${door}`
      // spec-74 B11: a v2 biweekly knows whether it came in on time.
      return `Biweekly-update ingeleverd${payload.on_time === false ? ' (te laat)' : ''}${door}`
    }
    case 'portal_milestone_ticked': {
      // spec-74 B8: "Founder vinkte T02 · Prototype getest af voor Fervonic" (shared "voor" suffix, chosen over the spec example "bij").
      const code = payload.item_code?.trim() || null
      const itemTitle = payload.item_title?.trim() || null
      const item = [code, itemTitle].filter(Boolean).join(' · ') || 'een mijlpaal'
      const actor = payload.actor_display?.trim() || 'Iemand'
      switch (payload.action) {
        case 'tick':
          return `${actor} vinkte ${item} af${voor}`
        case 'untick':
          return `${actor} zette ${item} terug op open${voor}`
        case 'manual_complete':
          return `${item} handmatig afgerond${voor}`
        case 'manual_uncomplete':
          return `Handmatige afronding van ${code ?? item} ingetrokken${voor}`
        case 'revert':
          return `Vinkje bij ${item} teruggedraaid${voor}`
        default:
          return `Mijlpaal bijgewerkt${voor}`
      }
    }
    case 'ai_budget_threshold': {
      // spec-78: "AI-verbruik: 81% van het maandbudget bereikt ($162,40 van $200)".
      const dollars = (value: string | undefined) => {
        const amount = Number(value)
        if (value == null || Number.isNaN(amount)) return '?'
        const whole = Number.isInteger(amount)
        return amount.toLocaleString('nl-NL', {
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: whole ? 0 : 2,
        })
      }
      return `AI-verbruik: ${payload.percent ?? '?'}% van het maandbudget bereikt ($${dollars(payload.total_usd)} van $${dollars(payload.budget_usd)})`
    }
    case 'wasstraat_item_completed':
      return `Wasstraat-item afgerond${voor}`
    default:
      return name ? `Melding voor ${name}` : 'Melding'
  }
}

